// =============================================================================
// ui/shell/FreeTierLimit.tsx — the dialog a capped create gets instead of a form.
// -----------------------------------------------------------------------------
// ── ⚠ INSTEAD OF THE FORM, NOT AFTER IT ──
//
// The refusal replaces the editor rather than appearing when the user hits Save.
// Letting somebody name a pattern, write its entry criteria and pick an exit
// model, and only then telling them the plan does not allow it, is the version
// of this that generates support mail.
//
// It is also the single choke point: each of the three registries has exactly
// ONE insert in the whole client with exactly ONE caller, and that caller is the
// modal this stands in for. There is no bulk path, no import path and no
// duplicate gesture to leak through — verified by enumerating the insert sites
// rather than by grepping for a keyword.
//
// ── ⚠ THE BUTTON STILL OPENS ──
//
// "+ New Pattern" is not disabled. A disabled control with no explanation is the
// discoverability failure, not a guard: the user cannot tell "I may not" from
// "it is broken". The old client keeps its buttons live and explains on click
// too — its toast fires from the create path, not from a greyed-out button.
//
// ⚠ NO NEW CSS. `.modal-scrim` / `.modal` / `.modal-head` / `.modal-body` /
// `.modal-foot` are the house classes, and `scripts/check-styles.mjs` fails the
// build for a class with no rule. Those five went unwritten once and a modal
// rendered inline with no overlay — invisible to the tests, which assert content
// and roles and have both.
// =============================================================================

export interface FreeTierLimitProps {
  /** What the user was trying to make, e.g. "New Pattern". */
  title: string;
  /** The refusal sentence, from `useFreeTierCap`. */
  message: string;
  onClose: () => void;
  /** Send them to the plan. Omitted where there is nowhere to send them from. */
  onSeePlans?: (() => void) | undefined;
}

export function FreeTierLimit({ title, message, onClose, onSeePlans }: FreeTierLimitProps) {
  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal" data-testid="free-tier-limit">
        <header className="modal-head">
          <b>{title}</b>
          {/* ⚠ Always closeable. The old client's free-tier suite spends
              assertions on exactly this for its pricing modal — a paywall that
              traps the reader is a bug report, not a conversion. */}
          <button type="button" className="btn" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="modal-body" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 34, lineHeight: 1 }} aria-hidden="true">🔒</div>
          <p style={{ margin: '10px 0 0', fontWeight: 600 }}>{message}</p>
          <p className="muted" style={{ fontSize: 13, lineHeight: 1.55, marginTop: 6 }}>
            Nothing you already have is affected — everything stays, and stays editable.
          </p>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn" onClick={onClose}>Close</button>
          {onSeePlans ? (
            <button type="button" className="btn primary" onClick={onSeePlans}>See plans</button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
